import { useStytchB2BClient } from '@stytch/react/b2b';
import { useQuery } from '@tanstack/react-query';
import gql from 'graphql-tag';
import { useMemo } from 'react';

import { type SolQueryParamsNew } from '@/components/InstantSearch';
import { useSolImmutableQuery } from '@/hooks/useSolQuery';
import { useMe } from '@/providers/User';

import fetcher, { SOL_API_HOST } from '../Fetcher';
import { type HttpResponse, HttpRequestError } from '../httpType';
import { fetcherSol } from '../sol-fetcher';
import {
  type InstanceCreateRestResponse,
  type InstanceDetailDataType,
  type InstanceGQLResponse,
  type InstanceListGQLResponse,
  type InstanceShortDataType,
  type InstanceUpdateGQLResponse,
  type InstanceUpdateVariablesType,
  type Organization,
  type PipelineReportMetadataGQLResponse,
  type SsoOrganizationsByEmailApiResponse,
} from './organizationType';

export const useCurrentInstance = () => {
  const { data, error, isLoading, mutate } = useSolImmutableQuery<
    InstanceGQLResponse<InstanceShortDataType>
  >({
    query: gql`
      query GetCurrentInstance {
        instances {
          current {
            id
            createdAt
            hasActiveCRMConnection
            name
            tam {
              audience {
                id
                name
                metrics {
                  size
                }
                isTAM: is_default
              }
            }
            tagInstallationDate
            pipelineReport {
              influenceWindow
            }
          }
        }
      }
    `,
  });

  return {
    instance: data?.instances.current,
    error,
    isLoading,
    mutate,
  };
};

export const useCurrentInstanceSettings = () => {
  const { data, error, isLoading, mutate } = useSolImmutableQuery<
    InstanceGQLResponse<InstanceDetailDataType>
  >({
    query: gql`
      query GetCurrentInstanceSettings {
        instances {
          current {
            id
            createdAt
            name
            tam {
              audience {
                id
                name
              }
            }
            code
            domain
            hasActiveCRMConnection
            pipelineReport {
              url
              fieldName
              influenceWindow
            }
            leadStatusOptions {
              id
              name
            }
            lead {
              offlineEventsLeadStatus
            }
            taskTypeOptions {
              id
              name
            }
            taskEvent {
              offlineEventsAll
              offlineTasksAll
              offlineTasksSpecific
            }
            typeFieldOptions {
              id
              name
            }
            spendFieldOptions {
              id
              name
            }
            campaign {
              spendField
              typeField
            }
            visitExclusions
          }
        }
      }
    `,
  });

  return {
    instanceDetails: data?.instances.current,
    error,
    isLoading,
    mutate,
  };
};

const DEFAULT_PIPELINE_INFLUENCE_WINDOW = 90;

export const mutateCurrentInstanceUpdate = async (accountSettings: InstanceDetailDataType) => {
  const pipelineReport =
    accountSettings.pipelineReport == null
      ? null
      : {
          ...accountSettings.pipelineReport,
          influenceWindow:
            accountSettings.pipelineReport.influenceWindow ?? DEFAULT_PIPELINE_INFLUENCE_WINDOW,
        };

  return await fetcherSol<InstanceUpdateVariablesType, InstanceUpdateGQLResponse>({
    query: gql`
      mutation UpdateCurrentInstance($instance: UpdateInstanceInput!) {
        instances {
          update(instance: $instance) {
            id
          }
        }
      }
    `,
    variables: {
      instance: {
        name: accountSettings.name,
        tam: {
          audienceId: accountSettings.tam.audience?.id,

          // @deprecated
          // Cannot remove these empty arrays until Sol removes them as required fields
          regions: [],
          revRanges: [],
          sectors: [],
        },
        pipelineReport,
        taskEvent: accountSettings.taskEvent ?? null,
        lead: accountSettings.lead ?? null,
        campaign: accountSettings.campaign ?? null,
        visitExclusions: accountSettings.visitExclusions,
      },
    },
  });
};

export const useOrganizationList = ({ searchQuery = '' }) => {
  const user = useMe();
  const stytch = useStytchB2BClient();

  const query = useQuery({
    queryKey: ['stytch', 'discoveredOrganizations', user?.currentOrg?.id],
    queryFn: async () => {
      const { discovered_organizations } = await stytch.discovery.organizations.list();
      return discovered_organizations;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Transform raw data into Organization objects
  const currentOrg = user?.currentOrg;
  const loggedInWithPassword = user?.authFactors.some((factor) => factor.type === 'password');
  const organizations = useMemo<Organization[] | undefined>(() => {
    if (!query.data) return undefined;

    return query.data.map((org) => {
      const { organization_id, organization_name, organization_slug } = org.organization;

      // Stytch has a bug for organizations in a cross-org password environment (which we have).
      // A member who logged in with a password is treated as though they cannot seamlessly switch
      // into another organization with an org exchange. This is incorrect, so this is a workaround
      // to treat them as authenticated in other password-enabled organizations.
      //
      // TO REPRODUCE: Log in with a password, then open the InstanceSwitcher component. It will
      // make a request to list organizations and will have `member_authenticated` set to false for
      // other orgs which support password authentication.
      const passwordAuthEnabled = org.primary_required?.allowed_auth_methods?.includes('password');
      const isMemberAuthenticated = !!(
        org.member_authenticated ||
        (loggedInWithPassword && passwordAuthEnabled)
      );

      return {
        id: organization_slug.replace(/^inst~/, 'inst:'),
        organizationId: organization_id,
        slug: organization_slug,
        name:
          currentOrg?.id === organization_id
            ? // prefer the instance name over the one stored in Stytch (which may be latent)
              currentOrg?.name
            : organization_name,
        isAuthenticated: isMemberAuthenticated,
      };
    });
  }, [query.data, currentOrg, loggedInWithPassword]);

  // Filter by search query
  const filteredOrganizations = useMemo(() => {
    if (!searchQuery || !organizations) {
      return organizations;
    }

    return organizations.filter((org) =>
      org.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, organizations]);

  return {
    organizations: filteredOrganizations,
    error: query.error,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};

/**
 * Fetches organization details by slug using the Stytch SDK.
 * This can be called even when the user is not authenticated.
 * @param orgSlug - The organization slug without the 'inst~' prefix
 */
export const useOrganizationBySlug = (orgSlug: string | undefined) => {
  const stytch = useStytchB2BClient();

  const query = useQuery({
    queryKey: ['stytch', 'organization', orgSlug],
    queryFn: async () => {
      const response = await stytch.organization.getBySlug({
        organization_slug: `inst~${orgSlug}`,
      });
      return response.organization ?? null;
    },
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  return {
    organization: query.data,
    error: query.error,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};

export const mutateOrganizationCreate = async (
  domain: string,
  intermediateSessionToken: string,
): Promise<HttpResponse<InstanceCreateRestResponse>> => {
  const response = await fetch(`${SOL_API_HOST}instances`, {
    body: JSON.stringify({ domain }),
    headers: {
      authorization: `Bearer ${intermediateSessionToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  const headers = Object.fromEntries(
    Array.from(response.headers.entries()).map(([key, value]) => [key.toLowerCase(), value]),
  );

  const data = (await response.json()) as InstanceCreateRestResponse;

  if (!response.ok) {
    throw new HttpRequestError(`Request failed with status ${response.status}`, {
      data,
      headers,
      status: response.status,
    });
  }

  return {
    data,
    headers,
    status: response.status,
  };
};

export const fetchSSOOrganizationsByEmail = async (
  email: string,
  signal?: AbortSignal,
): Promise<Organization[]> => {
  const response = await fetcher.requestSol<undefined, SsoOrganizationsByEmailApiResponse>({
    url: `organizations/${encodeURIComponent(email)}`,
    method: 'GET',
    signal,
  });

  return (response.data.organizations ?? []).map((org) => {
    const organizationSlug = org.organization_slug;
    const organizationId = org.organization_id;
    const organizationName = org.organization_name;

    return {
      id: organizationSlug.replace(/^inst~/, 'inst:'),
      organizationId,
      slug: organizationSlug,
      name: organizationName,
      isAuthenticated: false,
    };
  });
};

export const useLoginAsInstanceList = (tableParams?: SolQueryParamsNew) => {
  const { data, error, isLoading } = useSolImmutableQuery<InstanceListGQLResponse>(
    {
      query:
        tableParams &&
        gql`
          query Instances($page: Pagination!, $sort: [SortParamInput]!, $searchQuery: String) {
            instances {
              get(page: $page, sort: $sort, searchQuery: $searchQuery) {
                edges {
                  node {
                    id
                    name
                    createdAt
                    domain
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        ...tableParams,
        filter: null,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    instanceListData: data?.instances.get.edges.map((d) => d.node),
    totalResults: data?.instances.get.totalEdges,
    isLoading,
    error,
  };
};

export const useCRMPipelineReportMetadata = (reportURL?: string) => {
  const { data, error, isLoading } = useSolImmutableQuery<PipelineReportMetadataGQLResponse>(
    {
      query:
        !!reportURL &&
        gql`
          query GetPipelineReportMetadata($url: String!) {
            instances {
              getPipelineReportMetadata(url: $url) {
                currencyFieldNames
                name
              }
            }
          }
        `,
      variables: {
        url: reportURL,
      },
    },
    {
      retry: false,
    },
  );

  return {
    reportMetadata: data?.instances.getPipelineReportMetadata,
    isLoading,
    error,
  };
};
